/**
 * QQ机器人 API - 真实后端对接
 * OneBot 协议 / 群聊管理
 */

import { apiClient } from '@/lib/apiClient'

// ========== QQ机器人管理 ==========

export interface QQBot {
  id: string
  name: string
  botQq?: string
  wsUrl: string
  accessToken?: string
  status: 'online' | 'offline'
  groups: QQBotGroup[]
  createdAt: string
  updatedAt: string
}

export interface CreateQQBotInput {
  name: string
  botQq: string
  wsUrl: string
  accessToken?: string
}

export interface UpdateQQBotInput {
  name?: string
  wsUrl?: string
  accessToken?: string
}

/**
 * 获取QQ机器人列表
 */
export async function getQQBotList(): Promise<QQBot[]> {
  const response = await apiClient.get<{ code: number; data: QQBot[]; message: string }>('/admin/qq-bots')
  return response.data
}

/**
 * 获取QQ机器人详情
 */
export async function getQQBotDetail(botId: string): Promise<QQBot> {
  const response = await apiClient.get<{ code: number; data: QQBot; message: string }>(`/admin/qq-bots/${botId}`)
  return response.data
}

/**
 * 创建QQ机器人
 */
export async function createQQBot(input: CreateQQBotInput): Promise<QQBot> {
  const response = await apiClient.post<{ code: number; data: QQBot; message: string }>('/admin/qq-bots', input)
  return response.data
}

/**
 * 更新QQ机器人
 */
export async function updateQQBot(
  botId: string,
  input: UpdateQQBotInput
): Promise<QQBot> {
  const response = await apiClient.put<{ code: number; data: QQBot; message: string }>(`/admin/qq-bots/${botId}`, input)
  return response.data
}

/**
 * 删除QQ机器人
 */
export async function deleteQQBot(botId: string): Promise<void> {
  await apiClient.delete(`/admin/qq-bots/${botId}`)
}

/**
 * 重新连接QQ机器人
 */
export async function reconnectQQBot(botId: string): Promise<{ success: boolean }> {
  const response = await apiClient.post<{ code: number; data: { success: boolean }; message: string }>(`/admin/qq-bots/${botId}/reconnect`)
  return response.data
}

// ========== 群聊管理 ==========

export interface QQBotGroup {
  id?: string
  groupId: string
  groupName: string
  groupJoinLink?: string
  botId: string
  autoApprove: boolean
  welcomeEnabled?: boolean
  welcomeMessage?: string
  whitelistKeywords: string[]
  blacklistKeywords: string[]
  memberCount?: number
  maxMemberCount?: number
  sortOrder?: number
  isFull?: boolean
  createdAt: string
}

export interface AvailableQQGroup {
  groupId: string
  groupName: string
  memberCount?: number
  maxMemberCount?: number
  exists: boolean
}

export interface UpdateGroupConfigInput {
  autoApprove?: boolean
  groupJoinLink?: string
  welcomeEnabled?: boolean
  welcomeMessage?: string
  whitelistKeywords?: string[]
  blacklistKeywords?: string[]
}

export interface CreateQQGroupInput {
  botId: string
  groupId: string
  groupName: string
  autoApprove?: boolean
  memberCount?: number
  maxMemberCount?: number
}

/**
 * 获取群聊列表
 */
export async function getGroupList(botId?: string): Promise<QQBotGroup[]> {
  const url = botId ? `/admin/qq-bots/groups?botId=${botId}` : '/admin/qq-bots/groups'
  const response = await apiClient.get<{ code: number; data: QQBotGroup[]; message: string }>(url)
  return response.data
}

export async function getAvailableGroupList(botId: string): Promise<AvailableQQGroup[]> {
  const response = await apiClient.get<{ code: number; data: AvailableQQGroup[]; message: string }>(
    `/admin/qq-bots/${botId}/available-groups`
  )
  return response.data
}

/**
 * 获取群聊详情
 */
export async function getGroupDetail(groupId: string): Promise<QQBotGroup> {
  const response = await apiClient.get<{ code: number; data: QQBotGroup; message: string }>(`/admin/qq-bots/groups/${groupId}`)
  return response.data
}

/**
 * 更新群聊配置
 */
export async function updateGroupConfig(
  groupId: string,
  input: UpdateGroupConfigInput
): Promise<QQBotGroup> {
  const response = await apiClient.put<{ code: number; data: QQBotGroup; message: string }>(`/admin/qq-bots/groups/${groupId}`, input)
  return response.data
}

/**
 * 刷新群聊列表（从OneBot获取最新群列表）
 */
export async function refreshGroupList(botId: string): Promise<QQBotGroup[]> {
  const response = await apiClient.post<{ code: number; data: QQBotGroup[]; message: string }>(`/admin/qq-bots/${botId}/refresh-groups`)
  return response.data
}

export async function createQQGroup(input: CreateQQGroupInput): Promise<QQBotGroup> {
  const response = await apiClient.post<{ code: number; data: QQBotGroup; message: string }>(
    `/admin/qq-bots/${input.botId}/groups`,
    {
      groupId: input.groupId,
      groupName: input.groupName,
      autoApprove: input.autoApprove ?? false,
      memberCount: input.memberCount,
      maxMemberCount: input.maxMemberCount,
    }
  )
  return response.data
}

export async function deleteQQGroup(groupId: string): Promise<void> {
  await apiClient.delete(`/admin/qq-bots/groups/${groupId}`)
}

export async function moveQQGroupSortOrder(
  groupId: string,
  direction: 'up' | 'down'
): Promise<void> {
  await apiClient.post(`/admin/qq-bots/groups/${groupId}/sort`, { direction })
}

// ========== 验证码管理 ==========

export interface VerificationCode {
  id: string
  qqNumber: string
  code: string
  type: 'register' | 'reset_password'
  status: 'pending' | 'verified' | 'expired'
  createdAt: string
  expiresAt: string
  verifiedAt?: string
}

export interface PendingMember {
  id: string
  qqNumber: string
  nickname: string
  answers: string[]
  applyTime: string
  status: 'pending' | 'approved' | 'rejected'
}

export interface ReviewLog {
  id: string
  groupId: string
  qqNumber: string
  nickname: string
  answers: string[]
  comment?: string
  action: 'approve' | 'reject'
  reason?: string
  operator: string
  createdAt: string
}

/**
 * 获取验证码列表
 */
export async function getVerificationCodeList(params?: {
  status?: string
  type?: string
}): Promise<VerificationCode[]> {
  const queryParams = new URLSearchParams()
  if (params?.status) queryParams.set('status', params.status)
  if (params?.type) queryParams.set('type', params.type)

  const response = await apiClient.get<{ code: number; data: VerificationCode[]; message: string }>(
    `/admin/qq-bots/verification-codes?${queryParams.toString()}`
  )
  return response.data
}

/**
 * 手动验证验证码（管理员强制通过）
 */
export async function manualVerifyCode(
  codeId: string
): Promise<{ success: boolean }> {
  const response = await apiClient.post<{ code: number; data: { success: boolean }; message: string }>(
    `/admin/qq-bots/verification-codes/${codeId}/verify`
  )
  return response.data
}

export async function getPendingMembers(params?: {
  groupId?: string
}): Promise<PendingMember[]> {
  const queryParams = new URLSearchParams()
  if (params?.groupId) queryParams.set('groupId', params.groupId)

  const suffix = queryParams.toString() ? `?${queryParams.toString()}` : ''
  const response = await apiClient.get<{ code: number; data: PendingMember[]; message: string }>(
    `/admin/qq-bots/pending-members${suffix}`
  )
  return response.data
}

export async function reviewMember(params: {
  requestId: string
  approve: boolean
  reason?: string
}): Promise<void> {
  await apiClient.post(`/admin/qq-bots/pending-members/${params.requestId}/review`, {
    approve: params.approve,
    reason: params.reason,
  })
}

export async function getReviewLogs(params?: {
  groupId?: string
  limit?: number
}): Promise<ReviewLog[]> {
  const queryParams = new URLSearchParams()
  if (params?.groupId) queryParams.set('groupId', params.groupId)
  if (params?.limit) queryParams.set('limit', params.limit.toString())

  const suffix = queryParams.toString() ? `?${queryParams.toString()}` : ''
  const response = await apiClient.get<{ code: number; data: ReviewLog[]; message: string }>(
    `/admin/qq-bots/review-logs${suffix}`
  )
  return response.data
}

// ========== 消息日志 ==========

export interface MessageLog {
  id: string
  botId: string
  groupId: string
  userId: string
  message: string
  messageType: 'group' | 'private'
  createdAt: string
}

/**
 * 获取消息日志
 */
export async function getMessageLogs(params?: {
  botId?: string
  groupId?: string
  limit?: number
}): Promise<MessageLog[]> {
  const queryParams = new URLSearchParams()
  if (params?.botId) queryParams.set('botId', params.botId)
  if (params?.groupId) queryParams.set('groupId', params.groupId)
  if (params?.limit) queryParams.set('limit', params.limit.toString())

  const response = await apiClient.get<{ code: number; data: MessageLog[]; message: string }>(
    `/admin/qq-bots/message-logs?${queryParams.toString()}`
  )
  return response.data
}

// ========== 机器人统计 ==========

export interface BotStatistics {
  totalBots: number
  onlineBots: number
  totalGroups: number
  totalMembers: number
  messagesToday: number
  verificationsToday: number
}

/**
 * 获取机器人统计
 */
export async function getBotStatistics(): Promise<BotStatistics> {
  const response = await apiClient.get<{ code: number; data: BotStatistics; message: string }>('/admin/qq-bots/statistics')
  return response.data
}
